<template>
  <div class="d-flex flex-column min-vh-100" :style="{ color: colorPrimary }">
    <LoadingSpinner :isVisible="loading"></LoadingSpinner>
    <header :style="{ backgroundColor: backgroundPrimary }">
      <nav class="navbar">
        <div class="container-fluid">
          <a class="navbar-brand" :href="config?.tenantInfo?.homePage" target="_blank">
            <img v-if="logoUrl" :src="logoUrl" height="38" class="d-inline-block align-text-top">
          </a>
          <div class="btn-group">
            <button :style="{ color: colorPrimary }" class="btn btn-transparent dropdown-toggle"
              data-bs-toggle="dropdown">
              {{ locale }}
            </button>
            <ul class="dropdown-menu" aria-labelledby="dropdownMenuButton">
              <li v-for="optionLocale in supportLocales" :key="`locale-${optionLocale}`"><a class="dropdown-item"
                  @click="locale = optionLocale" href="#">{{ optionLocale }}</a></li>
            </ul>
          </div>
        </div>
      </nav>
    </header>
    <main role="main" class="flex-grow-1 mt-4 mb-4">
      <div class="container">
        <header v-if="!loading" style="border-radius: 1.5rem; margin: 0; padding: 3rem 3rem 14rem;"
          :style="{ backgroundColor: backgroundPrimary, color: colorPrimary }">
          <div v-if="notFoundError || isInvalid">
            <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="128" height="128"
              class="bi bi-emoji-frown" viewBox="0 0 16 16">
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
              <path
                d="M4.285 12.433a.5.5 0 0 0 .683-.183A3.498 3.498 0 0 1 8 10.5c1.295 0 2.426.703 3.032 1.75a.5.5 0 0 0 .866-.5A4.498 4.498 0 0 0 8 9.5a4.5 4.5 0 0 0-3.898 2.25.5.5 0 0 0 .183.683zM7 6.5C7 7.328 6.552 8 6 8s-1-.672-1-1.5S5.448 5 6 5s1 .672 1 1.5zm4 0c0 .828-.448 1.5-1 1.5s-1-.672-1-1.5S9.448 5 10 5s1 .672 1 1.5z" />
            </svg>
            <div v-if="isInvalid">
              <h2 class="mt-5" style="font-size: 2.6rem;text-transform:uppercase;font-weight:600">{{
                $t('yourWalletInvalid.heading') }}</h2>
              <h3 class="mt-3" v-html="$t('yourWalletInvalid.subHeading')"></h3>
              <p class="mt-5 custom-link" style="font-size: 1.25rem;" v-html="$t('yourWalletInvalid.description')"></p>
            </div>
            <div v-else-if="notFoundError">
              <h2 class="mt-5" style="font-size: 2.6rem;text-transform:uppercase;font-weight:600">{{
                $t('passNotFound.heading') }}</h2>
              <h3>{{ $t('passNotFound.subHeading') }}</h3>
              <p class="mt-3" style="font-size: 1.25rem">{{ $t('passNotFound.description') }}</p>
            </div>
          </div>
          <div v-else>
            <h2 style="font-size: 2.6rem;text-transform:uppercase;font-weight:600">{{
              headingText }}</h2>
            <p class="mt-3" style="font-size: 1.25rem">{{ descriptionText }}</p>
          </div>
        </header>
        <div v-if="!notFoundError && !isInvalid" class="container" style="margin-top: -13rem;">
          <div class="row gx-5 m-md-1" :style="{ color: colorSecondary }">
            <div class="col-md mt-4" v-if="config?.appleWallet && isAppleDevice() && isMobile()">
              <div class="d-flex justify-content-center" style="height: 100%;">
                <article class="p-4"
                  style="border-radius: 1rem; min-width: 200px; max-width: 580px; display: flex; flex-direction: column;  text-align: center;"
                  :style="{ backgroundColor: backgroundSecondary }">
                  <div>
                    <img src="@/assets/eLpass_iPhone_wallet_ikona.png" width="128" />
                  </div>
                  <h3>Apple Wallet</h3>
                  <p>{{ $t('content.appleWallet.description') }}</p>
                  <div style="margin-top: auto">                  
                    <a v-if="isAppleDevice() && isMobile()" :href="config?.downloadUrl + '?client=apple'" class="btn btn-primary align-bottom">
                      <IconDownload /> {{ $t('content.yourWallet.saveToPhone') }}
                    </a>
                  </div>
                </article>
              </div>
            </div>
            <div class="col-md mt-4" v-if="config?.googleWallet && !isAppleDevice() && isMobile()">
              <div class="d-flex justify-content-center" style="height: 100%;">
                <article class="p-4"
                  style="border-radius: 1rem; min-width: 200px; max-width: 580px; display: flex; flex-direction: column;  text-align: center;"
                  :style="{ backgroundColor: backgroundSecondary }">
                  <div>
                    <img src="@/assets/eLpass_Google_wallet_ikona.png" width="128" />
                  </div>
                  <h3>Google Wallet</h3>
                  <p>{{ $t('content.googleWallet.description') }}</p>
                  <div style="margin-top: auto">
                    <img v-if="!isMobile()" :src="config?.qrCodeGooglePay" height="168" />
                    <a :href="config?.googlePayUrl" class="btn btn-primary align-self-end btn-block">
                      <IconDownload /> {{ $t('content.googleWallet.saveToPhone') }}
                    </a>
                  </div>
                </article>
              </div>
            </div>
            <div class="col-md mt-4" v-if="config?.yourWallet && !isAppleDevice()">
              <div class="d-flex justify-content-center" style="height: 100%;">
                <article class="p-4" style="border-radius: 1rem; min-width: 200px; max-width: 580px;"
                  :style="{ backgroundColor: backgroundSecondary }">
                  <div>
                    <img src="@/assets/eLpass_Your_wallet_ikona.png" width="128" />
                  </div>
                  <h3>Yourwallet</h3>
                  <p v-html="$t('content.yourWallet.description')"></p>
                  <div style="margin-top: auto">
                    <img v-if="!isMobile()" :src="config?.qrCode" height="168" />
                    <a v-if="isMobile()" :href="ywDownloadUrl" class="btn btn-primary align-bottom">
                      <IconDownload /> {{ $t('content.yourWallet.saveToPhone') }}
                    </a>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
    <footer class="container-fluid flex-grow-0 flex-shrink-0 p-3"
      :style="{ backgroundColor: backgroundPrimary, color: colorPrimary }">
      <div class="row">
        <div class="col-12">
          {{ tenantText }}
        </div>
      </div>
      <div class="row pt-1">
        <div class="col-12">
          <a v-if="config?.tenantInfo?.socialLinks?.facebookUrl" :href="config?.tenantInfo?.socialLinks?.facebookUrl"
            target="_blank" :style="{ margin: '.4rem', color: colorPrimary }">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="currentColor" class="bi bi-meta"
              viewBox="0 0 16 16">
              <path fill-rule="evenodd"
                d="M8.217 5.243C9.145 3.988 10.171 3 11.483 3 13.96 3 16 6.153 16.001 9.907c0 2.29-.986 3.725-2.757 3.725-1.543 0-2.395-.866-3.924-3.424l-.667-1.123-.118-.197a54.944 54.944 0 0 0-.53-.877l-1.178 2.08c-1.673 2.925-2.615 3.541-3.923 3.541C1.086 13.632 0 12.217 0 9.973 0 6.388 1.995 3 4.598 3c.319 0 .625.039.924.122.31.086.611.22.913.407.577.359 1.154.915 1.782 1.714Zm1.516 2.224c-.252-.41-.494-.787-.727-1.133L9 6.326c.845-1.305 1.543-1.954 2.372-1.954 1.723 0 3.102 2.537 3.102 5.653 0 1.188-.39 1.877-1.195 1.877-.773 0-1.142-.51-2.61-2.87l-.937-1.565ZM4.846 4.756c.725.1 1.385.634 2.34 2.001A212.13 212.13 0 0 0 5.551 9.3c-1.357 2.126-1.826 2.603-2.581 2.603-.777 0-1.24-.682-1.24-1.9 0-2.602 1.298-5.264 2.846-5.264.091 0 .181.006.27.018Z" />
            </svg></a>
          <a v-if="config?.tenantInfo?.socialLinks?.instagramUrl" :href="config?.tenantInfo?.socialLinks?.instagramUrl"
            target="_blank" :style="{ margin: '.4rem', color: colorPrimary }">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="currentColor" class="bi bi-instagram"
              viewBox="0 0 16 16">
              <path
                d="M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.917 3.917 0 0 0-1.417.923A3.927 3.927 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.916 3.916 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.926 3.926 0 0 0-.923-1.417A3.911 3.911 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0h.003zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599.28.28.453.546.598.92.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.47 2.47 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.478 2.478 0 0 1-.92-.598 2.48 2.48 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233 0-2.136.008-2.388.046-3.231.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92.28-.28.546-.453.92-.598.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045v.002zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92zm-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217zm0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334z" />
            </svg></a>
          <a v-if="config?.tenantInfo?.socialLinks?.tiktokUrl" :href="config?.tenantInfo?.socialLinks?.tiktokkUrl"
            target="_blank" :style="{ margin: '.4rem', color: colorPrimary }">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="currentColor" class="bi bi-tiktok"
              viewBox="0 0 16 16">
              <path
                d="M9 0h1.98c.144.715.54 1.617 1.235 2.512C12.895 3.389 13.797 4 15 4v2c-1.753 0-3.07-.814-4-1.829V11a5 5 0 1 1-5-5v2a3 3 0 1 0 3 3V0Z" />
            </svg></a>
          <a v-if="config?.tenantInfo?.socialLinks?.twitterUrl" :href="config?.tenantInfo?.socialLinks?.twitterUrl"
            target="_blank" :style="{ margin: '.4rem', color: colorPrimary }">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="currentColor" class="bi bi-twitter"
              viewBox="0 0 16 16">
              <path
                d="M5.026 15c6.038 0 9.341-5.003 9.341-9.334 0-.14 0-.282-.006-.422A6.685 6.685 0 0 0 16 3.542a6.658 6.658 0 0 1-1.889.518 3.301 3.301 0 0 0 1.447-1.817 6.533 6.533 0 0 1-2.087.793A3.286 3.286 0 0 0 7.875 6.03a9.325 9.325 0 0 1-6.767-3.429 3.289 3.289 0 0 0 1.018 4.382A3.323 3.323 0 0 1 .64 6.575v.045a3.288 3.288 0 0 0 2.632 3.218 3.203 3.203 0 0 1-.865.115 3.23 3.23 0 0 1-.614-.057 3.283 3.283 0 0 0 3.067 2.277A6.588 6.588 0 0 1 .78 13.58a6.32 6.32 0 0 1-.78-.045A9.344 9.344 0 0 0 5.026 15z" />
            </svg>
          </a>
          <a v-if="config?.tenantInfo?.socialLinks?.linkedInUrl" :href="config?.tenantInfo?.socialLinks?.linkedInUrl"
            target="_blank" :style="{ margin: '.4rem', color: colorPrimary }">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="currentColor" class="bi bi-linkedin"
              viewBox="0 0 16 16">
              <path
                d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z" />
            </svg>
          </a>
        </div>
      </div>
      <div class="row pt-2">
        <div class="col-12">
          <small>&#169; {{ currentYear }} Powered by Markomat.</small>
        </div>
      </div>
    </footer>
  </div>
</template>
<script setup>
import { watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { SUPPORT_LOCALES as supportLocales, setI18nLanguage } from '../i18n';
import axios from 'axios'

const { locale } = useI18n({ useScope: 'global' });
watch(locale, (val) => {
  setI18nLanguage(val);
});
</script>

<script>
import LoadingSpinner from './LoadingSpinner.vue'
import IconDownload from './IconDownload.vue'
import mixin from "../mixins/mobiledetection.js";

export default {
  name: 'HomePage',
  mixins: [mixin],
  components: {
    LoadingSpinner,
    IconDownload
  },
  data() {
    return {
      id: this.$route.params.id,
      config: null,
      notFoundError: false,
      loading: true
    }
  },
  mounted() {
    this.notFoundError = !this.id;
    if (!this.notFoundError && !this.isInvalid) {
      axios.get(process.env.VUE_APP_ROOT_API + '/passdownloadpage/' + this.id)
        .then((response) => {
          this.config = response.data;
          document.body.style.backgroundColor = this.config.bodyColor;
          this.notFoundError = false;
          this.loading = false;
        })
        .catch((error) => {
          this.notFoundError = true;
          this.loading = false;
          console.log(error);
        });
    } else {
      this.loading = false;
    }
  },
  computed: {
    ywDownloadUrl() {
      var pk = this.config?.downloadUrl + '?client=yourwallet';            
      var ywIntent = pk.replace(/^(https?:)/, "intent:") +
           "#Intent;scheme=yourwallet;package=eu.yourpass.wallet;S.market_referrer=" + pk + ";end";                
      return ywIntent;   
    },
    currentYear() {
      return new Date().getFullYear();
    },
    logoUrl() {
      return this.config?.logoUrl;
    },
    tenantText() {
      const array = [];
      let ci = this.config?.tenantInfo;

      if (ci?.name)
        array.push(ci?.name);
      if (ci?.street)
        array.push(ci?.street);
      if (ci?.town)
        array.push(ci?.town);
      if (ci?.zip)
        array.push(ci?.zip);

      return array.join(', ');
    },
    headingText() {
      return this.config?.headingText[this.$i18n.locale];
    },
    descriptionText() {
      return this.config?.descriptionText[this.$i18n.locale];
    },
    bodyColor() {
      return this.config?.bodyColor || "#ececec";
    },
    backgroundPrimary() {
      return this.config?.backgroundPrimary || '#818589';
    },
    backgroundSecondary() {
      return this.config?.backgroundSecondary || '#fff';
    },
    colorPrimary() {
      return this.config?.colorPrimary || '#fff';
    },
    colorSecondary() {
      return this.config?.colorSecondary || '#000';
    },
    isInvalid() {
      return this.$route.path.includes('/invalid');
    },
  }
}
</script>
<style>
.btn-primary,
.btn-primary:hover,
.btn-primary:active,
.btn-primary:visited {
  background-color: v-bind("backgroundPrimary");
  border-color: v-bind("backgroundPrimary");
  color: v-bind("colorPrimary");
}

.custom-link a:link, .custom-link a:visited {
  color: white;
}

.custom-link a:hover, .custom-link a:active {
  color: black;
}

</style>